import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Autosave with the guarantees a table of money needs:
 *  - DEBOUNCED: a burst of edits is one save, `delay` ms after the last one.
 *  - SERIALIZED, newest wins: never two requests in flight. Every request carries the LATEST snapshot, so a slow older response can
 *    neither overwrite nor be mistaken for a newer one (out-of-order updates cannot happen).
 *  - NOTHING IS LOST: a failed save keeps the snapshot pending ('error'); the next edit, `retry()` or `flush()` sends it again, and
 *    leaving the page while something is unsaved asks first.
 *  - FLUSHED on unmount (changing tab / project) so the last edit still goes out.
 */
export type AutosaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

export function useAutosave<T>(save: (snapshot: T) => Promise<void>, { delay = 800 }: { delay?: number } = {}) {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;
  const latest = useRef<{ snapshot: T } | null>(null); // set while there is something not yet saved
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const running = useRef<Promise<void> | null>(null);
  const mounted = useRef(true);

  const setIfMounted = useCallback((next: AutosaveStatus, message: string | null = null) => {
    if (!mounted.current) return;
    setStatus(next);
    setError(message);
  }, []);

  const drain = useCallback(async (): Promise<void> => {
    if (running.current) return running.current;
    const run = (async () => {
      while (latest.current) {
        const pending = latest.current;
        latest.current = null;
        setIfMounted('saving');
        try {
          await saveRef.current(pending.snapshot);
        } catch (failure) {
          // keep it, unless a newer snapshot already replaced it
          if (!latest.current) latest.current = pending;
          setIfMounted('error', failure instanceof Error ? failure.message : 'Failed to save');
          return;
        }
      }
      setIfMounted('saved');
    })();
    running.current = run;
    try {
      await run;
    } finally {
      running.current = null;
    }
  }, [setIfMounted]);

  /** Call with the full latest state after every edit. */
  const schedule = useCallback((snapshot: T) => {
    latest.current = { snapshot };
    setIfMounted('unsaved');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void drain(); }, delay);
  }, [delay, drain, setIfMounted]);

  /** Save now what is pending (and wait for the request in flight). */
  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (running.current) await running.current;
    if (latest.current) await drain();
  }, [drain]);

  const hasUnsaved = useCallback(() => !!latest.current || !!running.current, []);

  /** Throws away what is pending (the user chose to reload the document): nothing stale can be sent afterwards. */
  const discard = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    latest.current = null;
    setIfMounted('idle');
  }, [setIfMounted]);

  useEffect(() => {
    mounted.current = true;
    const warn = (event: BeforeUnloadEvent) => {
      if (latest.current || running.current) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      mounted.current = false;
      window.removeEventListener('beforeunload', warn);
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      if (latest.current && !running.current) void drain(); // the last edit still goes out
    };
  }, [drain]);

  return { status, error, schedule, flush, retry: flush, hasUnsaved, discard };
}
